import React, {useCallback} from 'react';
import {StyleSheet, View} from 'react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import type {CamerasStackParamList} from '@/app/routes';
import {useAuthStore} from '@/entities/session/auth-store';
import {LiveView} from '@/features/view-live-video/ui/LiveView';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {Page} from '@/shared/ui/page';
import {spacing} from '@/shared/ui/theme';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';

/**
 * «Камеры» — своя вкладка, рядом с «Устройствами», и видна всегда.
 *
 * Камеры жили вторым сегментом в «Устройствах», и это было удобно, пока камеры были
 * дополнением к терминалам. Теперь просмотр — отдельное рабочее место (раскладки,
 * виды, карусель), его открывают каждый день, и прятать его за переключателем внутри
 * другого раздела — лишнее нажатие каждый раз (владелец, 2026-10-22: «устройства и
 * камеры раздели на два, и пусть они будут всегда видны»).
 *
 * Живое видео требует компании: `POST /stream/tokens` обязателен к `x-company-id`, и
 * токен, выпущенный в системной области, несёт компанию, которой не соответствует ни
 * одно устройство, — позже это приезжает как `480`, неотличимое от выдернутой камеры.
 *
 * Заголовок не печатается: «Камеры» написано на вкладке и в верхней полосе, а строка
 * над раскладкой на телефоне — минус ряд миниатюр.
 */
type Props = NativeStackScreenProps<CamerasStackParamList, 'CameraWall'>;

const styles = StyleSheet.create({
  wall: {flex: 1, paddingTop: spacing(4), paddingBottom: spacing(6)},
});

export function CamerasScreen({navigation, route}: Props) {
  const {t} = useTranslation();
  const {isDesktop} = useBreakpoint();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  // «Камеры отдела» из карточки отдела: отдел приезжает параметром и гасится, как
  // только раскладка его открыла, — иначе F5 открывал бы его снова поверх вида.
  const focusDepartment = route.params?.dept;
  const clearFocus = useCallback(
    () => navigation.setParams({dept: undefined}),
    [navigation],
  );
  // Подключение по QR живёт в «Устройствах»: там его экран и подтверждение.
  const openAdopt = useCallback(
    () => navigation.getParent()?.navigate('Devices', {screen: 'ScanDevice'}),
    [navigation],
  );

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.cameras" />;
  }

  return (
    // На широком окне — во всю высоту без прокрутки, как экран регистратора:
    // раскладка вписывается в то, что осталось. На телефоне листает страница.
    <Page
      scroll={!isDesktop}
      title={t('nav.cameras')}
      hideTitle
      testID="cameras-screen">
      <View style={isDesktop ? styles.wall : undefined}>
        <LiveView
          companyId={companyId}
          onOpenSettings={(sourceId, name) =>
            navigation.navigate('CameraSettings', {sourceId, name})
          }
          onAdopt={openAdopt}
          focusDepartment={focusDepartment}
          onFocusHandled={clearFocus}
        />
      </View>
    </Page>
  );
}
